import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import classNames from "classnames/bind";
import PromoStrip from "common/blocks/PromoStrip";
import SmartLink from "common/wrapper/SmartLink";
import { MotionConfig, Variants, motion } from "framer-motion";
import type { ResolvedStockNumberSearch } from "resolvers/base/stockNumberSearchResolver";
import type { ResolvedMegaMenuItem } from "resolvers/data/header/megaMenuResolver";
import { ContentTypeCodenames } from "services/cms/models/contentTypeCodenames";

import styles from "./HeaderMegaMenu.module.scss";
import HeaderMegaMenuCtaButtons from "./HeaderMegaMenuCtaButton";
import HeaderMegaMenuLinks from "./HeaderMegaMenuLinks";
import HeaderMegaMenuPromo from "./HeaderMegaMenuPromo";
import HeaderStockNumberSearch from "./HeaderStockNumberSearch";

const cx = classNames.bind(styles);

export type HeaderMegaMenuProps = {
    activeMenuId: string | null;
    megaMenuContent: ResolvedMegaMenuItem[];
    stockNumberSearch: ResolvedStockNumberSearch | null;
    prefetch?: boolean;
    changeFocusStockInput: (value: boolean) => void;
};

const CONTENT_COMPONENTS = {
    links: HeaderMegaMenuLinks,
    banners: HeaderMegaMenuPromo,
};

const HeaderMegaMenu: React.FC<HeaderMegaMenuProps> = ({
    activeMenuId,
    megaMenuContent,
    stockNumberSearch,
    prefetch = false,
    changeFocusStockInput,
}) => {
    const [currentOpenHeight, setCurrentOpenHeight] = useState(0);
    const tabRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
    const currentMegaMenuContent = activeMenuId
        ? megaMenuContent.find((item) => item.id === activeMenuId)
        : undefined;

    const { link, text } = currentMegaMenuContent?.promo ?? {};
    const hasPromo = !!(link || text);
    const promoHeight = hasPromo ? 35 : 0;

    const onResize = useCallback(() => {
        if (!activeMenuId) return;
        const element = tabRefs.current[activeMenuId];
        const withoutHeader = window.innerHeight - 150;
        const height = element?.scrollHeight;

        if ((height || height === 0) && withoutHeader < 1000) {
            setCurrentOpenHeight(withoutHeader > height ? height + promoHeight : withoutHeader);
        }
    }, [activeMenuId, promoHeight]);

    useEffect(() => {
        window.addEventListener("resize", onResize);
        onResize();

        return () => {
            window.removeEventListener("resize", onResize);
        };
    }, [onResize]);

    const megaMenuVariants: Variants = {
        closed: {
            opacity: 0,
            height: 0,
            transition: {
                duration: 0,
                ease: "linear",
            },
        },
        open: {
            opacity: 1,
            height: currentOpenHeight || "max-content",
            transition: {
                duration: 0,
                ease: "linear",
            },
        },
        hide: {
            opacity: 0,
            transition: {
                duration: 0,
                ease: "linear",
            },
        },
        show: {
            opacity: 1,
            transition: {
                duration: 0.5,
                ease: "easeInOut",
            },
        },
    };

    const tabSections = useMemo(() => {
        return megaMenuContent?.reduce((result, { id, sections }) => {
            sections?.forEach((section, index) => {
                const { title, body, ctaButtons } = section;
                const hasHeader = !!title?.text?.trim() || (ctaButtons?.body?.length ?? 0) > 0;

                result[`${id}_${section.type}_${index}`] = (
                    <>
                        {hasHeader && (
                            <div className={cx("header")}>
                                {ctaButtons?.position === "top" && section.width === "w1" && (
                                    <HeaderMegaMenuCtaButtons sectionContent={ctaButtons} />
                                )}
                                {title?.text?.trim() && (
                                    <span className={cx("sectionHeading")}>
                                        {title?.link ? (
                                            <SmartLink href={title.link} prefetch={!!prefetch}>
                                                <a
                                                    target={title.target}
                                                    className={cx("sectionHeadingLink")}
                                                >
                                                    {title.text}
                                                </a>
                                            </SmartLink>
                                        ) : (
                                            <span className={cx("sectionHeadingLink")}>
                                                {title?.text}
                                            </span>
                                        )}
                                    </span>
                                )}
                            </div>
                        )}
                        {body?.map((sectionContent: any, index: number) => {
                            const Component =
                                CONTENT_COMPONENTS[section.type as unknown as "links" | "banners"];

                            if (!Component) return null;

                            return (
                                <Component
                                    key={`${section.type}_${index}`}
                                    sectionContent={sectionContent}
                                >
                                    {ctaButtons?.position === "bottom" &&
                                        section.width !== "w1" && (
                                            <HeaderMegaMenuCtaButtons sectionContent={ctaButtons} />
                                        )}
                                </Component>
                            );
                        })}
                        {ctaButtons?.position === "bottom" && section.width === "w1" && (
                            <HeaderMegaMenuCtaButtons sectionContent={ctaButtons} />
                        )}
                    </>
                );
            });

            return result;
        }, {} as any);
    }, [megaMenuContent, prefetch]);

    // useDisableScroll(!!activeMenuId);

    return (
        <MotionConfig transition={{ type: "just" }} reducedMotion="user">
            <motion.div
                animate={activeMenuId ? "open" : "closed"}
                initial="closed"
                variants={megaMenuVariants}
                className={cx("megaMenu")}
            >
                <div className={cx("wrapper")}>
                    {megaMenuContent?.map(({ id, sections, promo }) => {
                        return (
                            <motion.div
                                initial="hide"
                                animate={activeMenuId === id ? "show" : "hide"}
                                variants={megaMenuVariants}
                                key={id}
                                className={cx("tab", {
                                    hide: activeMenuId !== id,
                                })}
                            >
                                <div
                                    ref={(el) => {
                                        tabRefs.current[id] = el;
                                    }}
                                    className={cx("tabContent")}
                                >
                                    <div className={cx("tabContentWrapper")}>
                                        {sections?.map((section, index) => {
                                            return (
                                                <div
                                                    key={`${id}_${section.type}_${index}`}
                                                    className={cx(
                                                        "column",
                                                        section.type,
                                                        section.width
                                                    )}
                                                >
                                                    {section.background && (
                                                        <div
                                                            className={cx({
                                                                background: section.background,
                                                            })}
                                                        >
                                                            <div
                                                                style={{
                                                                    height: currentOpenHeight + 50,
                                                                }}
                                                            />
                                                        </div>
                                                    )}
                                                    {tabSections[`${id}_${section.type}_${index}`]}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                                {(!!promo?.link || !!promo?.text) && <PromoStrip promo={promo} />}
                            </motion.div>
                        );
                    })}
                    {stockNumberSearch && (
                        <motion.div
                            initial="hide"
                            animate={
                                activeMenuId === ContentTypeCodenames.StockNumberSearch
                                    ? "show"
                                    : "hide"
                            }
                            variants={megaMenuVariants}
                            key={ContentTypeCodenames.StockNumberSearch}
                            className={cx("tab", {
                                hide: activeMenuId !== ContentTypeCodenames.StockNumberSearch,
                            })}
                        >
                            <div
                                ref={(el) => {
                                    tabRefs.current[ContentTypeCodenames.StockNumberSearch] = el;
                                }}
                                className={cx("tabContent")}
                            >
                                <div className={cx("tabContentWrapper")}>
                                    <HeaderStockNumberSearch
                                        stockNumberSearch={stockNumberSearch}
                                        onResize={onResize}
                                        changeFocusStockInput={changeFocusStockInput}
                                    />
                                </div>
                            </div>
                        </motion.div>
                    )}
                </div>
            </motion.div>
        </MotionConfig>
    );
};

export default HeaderMegaMenu;
